



















































































































































































































import { ClientAccountsApi } from '@/api/client-accounts';
import willExecutorDetail from '@/components/client/view-client/will-executor-detail.vue';
import willGuardianDetail from '@/components/client/view-client/will-guardian-detail.vue';
import { ClientAccount } from '@/shared/clients/client-account';
import { ClientAccountWill } from '@/shared/clients/client-account-will';
import { ClientAccountWillExecutor, createClientAccountWillExecutor } from '@/shared/clients/client-account-will-executor';
import { ClientAccountWillGuardian, createClientAccountWillGuardian } from '@/shared/clients/client-account-will-guardian';
import { WillType } from '@/shared/types/will-type';
import { User } from '@/shared/user';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const typesModule = namespace('types');
const usersModule = namespace('users');
const clientApi = new ClientAccountsApi();

@Component({
    components: {
        willExecutorDetail,
        willGuardianDetail,
    },
})
export default class EditWillHistoryItemDialog extends Vue {
    @Prop({ required: true }) public client!: ClientAccount;
    @Prop({ required: true }) public will!: ClientAccountWill;
    @Prop({ required: true }) public visible!: boolean;

    @typesModule.Getter('activeWillTypes') private willTypes!: Array<WillType>;

    @usersModule.Getter('users') private users!: Array<User>;

    private isVisible: boolean = this.visible;
    private isSaving: boolean = false;
    private currentExecutor: ClientAccountWillExecutor | null = null;
    private showExecutorsForm: boolean = false;
    private isEditingExecutor: boolean = false;
    private currentGuardian: ClientAccountWillGuardian | null = null;
    private showGuardiansForm: boolean = false;
    private isEditingGuardian: boolean = false;

    private get orderedWillExecutors(): Array<ClientAccountWillExecutor> {
        return this.will.executors.sort((n1, n2) => {
            if (n1.displayOrder! > n2.displayOrder!) { return 1; }
            if (n1.displayOrder! < n2.displayOrder!) { return -1; }
            return 0;
        });
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.$validator.errors.clear();
        this.isVisible = visible;
    }

    private userName(userId: string | null): string {
        if (userId == null) {
            return '';
        }

        const user = this.users.find((u) => u.id === userId);
        return user == null ? userId : `${user.firstName == null ? '' : user.firstName} ${user.lastName == null ? '' : user.lastName}`;
    }

    private async onSave(): Promise<void> {
        const isValid = this.$validator.validateAll();

        if (!isValid) {
            return;
        }

        this.isSaving = true;

        try {
            this.$emit('ok', { ...this.will, executors: [...this.will.executors], guardians: [...this.will.guardians] });
        } catch (e) {
            this.$snackbar.open({ message: `Could not update will. Please try again.`, type: 'is-danger' });
        }

        this.isSaving = false;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }

    private async onSaveGuardian(): Promise<void> {
        const isValid = await (this.$refs.guardian as Vue).$validator.validateAll('guardian-details');

        if (!isValid) {
            return;
        }

        if (!this.isEditingGuardian) {
            this.will.guardians.push({ ...this.currentGuardian! });
        } else {
            const index = this.will.guardians.findIndex((g) => g.id === this.currentGuardian!.id);

            if (index < 0) {
                this.$snackbar.open({ message: 'Could not update guardian. Please try again.', type: 'is-danger' });
            } else {
                if (this.currentGuardian!.id === 'editing') {
                    this.currentGuardian!.id = null;
                }

                this.will.guardians.splice(index, 1, { ...this.currentGuardian! });
            }
        }

        this.currentGuardian = null;
        this.showGuardiansForm = false;
        this.isEditingGuardian = false;
    }

    private onAddGuardian(): void {
        this.currentGuardian = createClientAccountWillGuardian();
        this.showGuardiansForm = true;
    }

    private onEditGuardian(guardian: ClientAccountWillGuardian): void {
        if (guardian.id == null) {
            guardian.id = 'editing';
        }

        this.currentGuardian = { ...guardian };
        this.showGuardiansForm = true;
        this.isEditingGuardian = true;
    }

    private onRemoveGuardian(guardian: ClientAccountWillGuardian): void {
        if (guardian.id == null) {
            guardian.id = 'editing';
        }

        const index = this.will.guardians.findIndex((g) => g.id === guardian.id);

        if (index < 0) {
            this.$snackbar.open({ message: 'Could not remove guardian. Please try again.', type: 'is-danger' });
        } else {
            this.will.guardians.splice(index, 1);
        }

        this.currentGuardian = null;
    }

    private onCancelGuardian(): void {
        for (const guardian of this.will.guardians.filter((g) => g.id === 'editing')) {
            guardian.id = null;
        }

        this.currentGuardian = null;
        this.showGuardiansForm = false;
        this.isEditingGuardian = false;
    }

    private async onSaveExecutor(): Promise<void> {
        const isValid = await (this.$refs.executor as Vue).$validator.validateAll('executor-details');

        if (!isValid) {
            return;
        }

        if (!this.isEditingExecutor) {
            this.will.executors.push({ ...this.currentExecutor! });
        } else {
            const index = this.will.executors.findIndex((e) => e.id === this.currentExecutor!.id);

            if (index < 0) {
                this.$snackbar.open({ message: 'Could not update executor. Please try again.', type: 'is-danger' });
            } else {
                if (this.currentExecutor!.id === 'editing') {
                    this.currentExecutor!.id = null;
                }

                this.will.executors.splice(index, 1, { ...this.currentExecutor! });
            }
        }

        this.currentExecutor = null;
        this.showExecutorsForm = false;
        this.isEditingExecutor = false;
    }

    private onAddExecutor(): void {
        this.currentExecutor = createClientAccountWillExecutor();
        this.showExecutorsForm = true;
    }

    private onEditExecutor(executor: ClientAccountWillExecutor): void {
        if (executor.id == null) {
            executor.id = 'editing';
        }

        this.currentExecutor = { ...executor };
        this.showExecutorsForm = true;
        this.isEditingExecutor = true;
    }

    private onRemoveExecutor(executor: ClientAccountWillExecutor): void {
        if (executor.id == null) {
            executor.id = 'editing';
        }

        const index = this.will.executors.findIndex((e) => e.id === executor.id);

        if (index < 0) {
            this.$snackbar.open({ message: 'Could not remove executor. Please try again.', type: 'is-danger' });
        } else {
            this.will.executors.splice(index, 1);
        }

        this.currentExecutor = null;
    }

    private onCancelExecutor(): void {
        for (const executor of this.will.executors.filter((e) => e.id === 'editing')) {
            executor.id = null;
        }

        this.currentExecutor = null;
        this.showExecutorsForm = false;
        this.isEditingExecutor = false;
    }

    private firstOrSubstituteExecutorTypeDescription(executor: ClientAccountWillExecutor): string | null {
        if (executor == null || executor.isFirstOrSubstituteExecutor == null) {
            return null;
        }

        return executor.isFirstOrSubstituteExecutor ? 'Sub Exec' : 'First Exec';
    }

    private soleOrCoExecutorTypeDescription(executor: ClientAccountWillExecutor): string | null {
        if (executor == null || executor.isSoleOrCoExecutor == null) {
            return null;
        }

        return executor.isSoleOrCoExecutor ? 'Co-Exec' : 'Sole Exec';
    }
}
